import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getAllSyncIntegratedSystems, SyncIntegratedSystem } from "../api/sync";
import { useAuth } from "./useAuth";

interface SyncIntegratedSystemsContextType {
  syncIntegratedSystems: SyncIntegratedSystem[];
  isLoading: boolean;
  /**
   * True once a fetch has completed at least once. An empty list on its own
   * cannot tell "this firm has no sync groups" from "we have not asked yet",
   * so any caller that wants to say "not found" needs this to be true first.
   */
  hasLoaded: boolean;
  /**
   * True when the last fetch failed. Without this, a failed request looks
   * identical to a successful empty one — `hasLoaded` goes true with an empty
   * list, and callers conclude the id they were given does not exist. That
   * turns an outage into "your sync group was deleted", which is a durable
   * claim about the customer's data made from a transient error.
   *
   * It says the last attempt failed, not that the list is unusable: a failed
   * refetch leaves the previous results in place, so read it alongside what
   * you were actually looking for rather than as a reason to stop.
   */
  hasError: boolean;
  refetch: () => void;
}

const SyncIntegratedSystemsContext = createContext<
  SyncIntegratedSystemsContextType | undefined
>(undefined);

interface SyncIntegratedSystemsProviderProps {
  children: React.ReactNode;
}

export const SyncIntegratedSystemsProvider: React.FC<SyncIntegratedSystemsProviderProps> =
  ({ children }) => {
    const { isLoggedIn } = useAuth();
    const [syncIntegratedSystems, setSyncIntegratedSystems] = useState<
      SyncIntegratedSystem[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    /*
     * Which fetch is the current one. Two of these can be in flight at once —
     * the login effect, a retry, and the refetch that follows creating a sync
     * pairing all call the same function — and they do not resolve in order.
     * Without this, a request that was already superseded can reject after the
     * newer one succeeded and set `hasError` over data we have just loaded.
     */
    const currentRequest = useRef(0);

    const refetch = useCallback(() => {
      const request = ++currentRequest.current;
      const isCurrent = () => request === currentRequest.current;

      setIsLoading(true);
      setHasError(false);
      getAllSyncIntegratedSystems()
        .then((systems) => {
          if (isCurrent()) {
            setSyncIntegratedSystems(systems);
          }
        })
        .catch((err) => {
          // Logged either way — a superseded failure is still worth seeing —
          // but only the current attempt is allowed to say the state is bad.
          console.error(err);
          if (isCurrent()) {
            setHasError(true);
          }
        })
        .finally(() => {
          if (isCurrent()) {
            setIsLoading(false);
            setHasLoaded(true);
          }
        });
    }, []);

    useEffect(() => {
      if (isLoggedIn) {
        refetch();
      }
    }, [isLoggedIn, refetch]);

    const value = useMemo(
      () => ({
        syncIntegratedSystems,
        isLoading,
        hasLoaded,
        hasError,
        refetch,
      }),
      [syncIntegratedSystems, isLoading, hasLoaded, hasError, refetch]
    );

    return (
      <SyncIntegratedSystemsContext.Provider value={value}>
        {children}
      </SyncIntegratedSystemsContext.Provider>
    );
  };

export const useSyncIntegratedSystems =
  (): SyncIntegratedSystemsContextType => {
    const context = useContext(SyncIntegratedSystemsContext);
    if (context === undefined) {
      throw new Error(
        "useSyncIntegratedSystems must be used within a SyncIntegratedSystemsProvider"
      );
    }
    return context;
  };

export const useSyncIntegratedSystem = (syncGroupId: number | undefined) => {
  const { syncIntegratedSystems, isLoading, hasLoaded, hasError, refetch } =
    useSyncIntegratedSystems();
  const syncIntegratedSystem = useMemo(
    () =>
      syncGroupId
        ? syncIntegratedSystems.find(
            (entry) => entry.sync_group_id === syncGroupId
          )
        : undefined,
    [syncIntegratedSystems, syncGroupId]
  );
  return { syncIntegratedSystem, isLoading, hasLoaded, hasError, refetch };
};
